import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Info, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { formatZAR } from '../../utils/currency';
import { Button } from '../ui/Button';
const WHATSAPP_NUMBER = '27732736906'; // Format: country code + number without leading 0
export function CartView() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    cartTotal
  } = useCart();
  const navigate = useNavigate();
  const handleAskAboutProduct = (productName: string) => {
    const message = `Hi! I have ${productName} in my cart and I'd like to know more details about it.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  if (cart.length === 0) {
    return <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-12 text-center" data-id="element-1008">
        <ShoppingCart className="w-20 h-20 text-gray-300 mx-auto mb-4" data-id="element-1009" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1010">
          Your cart is empty
        </h3>
        <p className="text-gray-600 mb-6" data-id="element-1011">
          Add some beautiful gifts to get started!
        </p>
        <Button onClick={() => navigate('/')} data-id="element-1012">Browse Products</Button>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1013">
      <div className="bg-white rounded-2xl shadow-sm border border-rose-100 overflow-hidden" data-id="element-1014">
        <div className="p-6 border-b border-rose-100" data-id="element-1015">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1016">
            Shopping Cart ({cart.length} {cart.length === 1 ? 'item' : 'items'})
          </h2>
        </div>

        <div className="divide-y divide-rose-50" data-id="element-1017">
          {cart.map((item, index) => <motion.div key={item.product.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="p-6 hover:bg-rose-50/30 transition-colors" data-id="element-1018">
              <div className="flex gap-4" data-id="element-1019">
                {/* Product Image */}
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-1020">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-1021" />
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0" data-id="element-1022">
                  <div className="flex justify-between items-start mb-2" data-id="element-1023">
                    <div data-id="element-1024">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1025">
                        {item.product.name}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-1026">
                        {item.product.category}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1027">
                      {/* Info Button - Ask on WhatsApp */}
                      <button onClick={() => handleAskAboutProduct(item.product.name)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors group relative" title="Ask about this product" data-id="element-1028">
                        <MessageCircle className="w-5 h-5" data-id="element-1029" />
                        <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-1030">
                          Ask on WhatsApp
                        </span>
                      </button>

                      {/* Remove Button */}
                      <button onClick={() => removeFromCart(item.product.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove from cart" data-id="element-1031">
                        <Trash2 className="w-5 h-5" data-id="element-1032" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4" data-id="element-1033">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3" data-id="element-1034">
                      <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled={item.quantity <= 1} data-id="element-1035">
                        <Minus className="w-4 h-4" data-id="element-1036" />
                      </button>
                      <span className="w-12 text-center font-semibold" data-id="element-1037">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-1038">
                        <Plus className="w-4 h-4" data-id="element-1039" />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right" data-id="element-1040">
                      <p className="text-sm text-gray-500" data-id="element-1041">
                        {formatZAR(item.product.price)} each
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1042">
                        {formatZAR(item.product.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Cart Summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-6" data-id="element-1043">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1044">Order Summary</h3>

        <div className="space-y-3 mb-6" data-id="element-1045">
          <div className="flex justify-between text-gray-600" data-id="element-1046">
            <span data-id="element-1047">Subtotal</span>
            <span className="font-medium" data-id="element-1048">{formatZAR(cartTotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-500" data-id="element-1049">
            <span data-id="element-1050">Delivery fee</span>
            <span data-id="element-1051">Calculated at checkout</span>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 mb-6" data-id="element-1052">
          <div className="flex justify-between items-center" data-id="element-1053">
            <span className="text-lg font-semibold text-gray-900" data-id="element-1054">Total</span>
            <span className="text-2xl font-bold text-rose-500" data-id="element-1055">
              {formatZAR(cartTotal)}
            </span>
          </div>
        </div>

        <Button onClick={() => navigate('/cart')} className="w-full" size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1057" />} data-id="element-1056">
          Proceed to Checkout
        </Button>

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1058">
          Need help? Click the{' '}
          <MessageCircle className="w-3 h-3 inline text-green-600" data-id="element-1059" /> icon on
          any product to ask us on WhatsApp
        </p>
      </div>
    </div>;
}